import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { CheckCircle2, ArrowRight, ExternalLink, Shield, Zap } from 'lucide-react';

const integrationData: Record<string, {
  name: string;
  logo: string;
  status: 'live' | 'coming-soon';
  seoTitle: string;
  seoDescription: string;
  keywords: string;
  tagline: string;
  longDescription: string;
  features: string[];
  useCases: string[];
  dataFlow: { from: string; to: string; description: string }[];
  security: string[];
  faq: { q: string; a: string }[];
}> = {
  'hubspot': {
    name: 'HubSpot',
    logo: '/logos/hubspot.svg',
    status: 'live',
    seoTitle: 'NoQ5 + HubSpot Integration | AI CRM Automation',
    seoDescription: 'Connect NoQ5 to HubSpot for autonomous CRM updates, evidence-based forecasting, and AI coaching. Deep bi-directional sync with HubSpot CRM v3 API.',
    keywords: 'HubSpot AI integration, HubSpot automation, HubSpot AI agent, HubSpot CRM automation, HubSpot deal forecasting, NoQ5 HubSpot',
    tagline: 'Deep bi-directional CRM sync with AI intelligence',
    longDescription: 'NoQ5 integrates deeply with HubSpot CRM to deliver autonomous data capture, evidence-based deal forecasting, and real-time coaching. Our bi-directional sync works with contacts, companies, deals, tickets, and all custom objects via the HubSpot CRM v3 API.',
    features: [
      'Bi-directional contact, company, and deal sync',
      'Custom object and field mapping support',
      'Workflow-compatible (HubSpot triggers fire normally)',
      'Properties API auto-discovery for custom fields',
      'Association labels and multi-object relationships',
      'Works alongside HubSpot Breeze AI',
      'Real-time webhook processing',
      'Full audit trail for every CRM change',
    ],
    useCases: [
      'Automatically log emails and meetings to deal records',
      'Update deal stages based on real buyer engagement',
      'Enrich contacts from email signatures and LinkedIn',
      'Generate evidence-based forecasts from HubSpot pipeline',
      'Deliver coaching nudges based on deal activity patterns',
    ],
    dataFlow: [
      { from: 'Email & Calendar', to: 'HubSpot', description: 'Meeting notes, email summaries, and activity logs synced automatically' },
      { from: 'HubSpot', to: 'NoQ5', description: 'Deal data, contact info, and pipeline stages ingested for analysis' },
      { from: 'NoQ5', to: 'HubSpot', description: 'AI-generated updates, enriched fields, and deal scores written back' },
    ],
    security: [
      'OAuth2 authentication via app.hubspot.com',
      'Granular scope permissions (only requested access)',
      'All data encrypted in transit (TLS 1.3) and at rest (AES-256)',
      'GDPR-compliant data processing within EU',
      'SOC 2 Type II aligned security practices',
    ],
    faq: [
      { q: 'Does NoQ5 conflict with HubSpot Breeze?', a: 'No. NoQ5 complements HubSpot Breeze by adding cross-platform intelligence from email, calendar, and meeting transcripts that Breeze does not access.' },
      { q: 'Which HubSpot plans work with NoQ5?', a: 'NoQ5 works with HubSpot Professional and Enterprise plans. Starter plans are supported with limited features.' },
      { q: 'Can NoQ5 update custom objects in HubSpot?', a: 'Yes. NoQ5 supports all custom objects and custom properties via the HubSpot CRM v3 API.' },
    ],
  },
  'salesforce': {
    name: 'Salesforce',
    logo: '/logos/salesforce.svg',
    status: 'live',
    seoTitle: 'NoQ5 + Salesforce Integration | AI Revenue Agent',
    seoDescription: 'Connect NoQ5 to Salesforce for AI-powered CRM automation, deal forecasting, and sales coaching. Works with Sales Cloud, custom objects, and Einstein.',
    keywords: 'Salesforce AI integration, Salesforce automation, Salesforce AI agent, Salesforce CRM AI, NoQ5 Salesforce, Salesforce Einstein alternative',
    tagline: 'AI revenue agent for Salesforce Sales Cloud',
    longDescription: 'NoQ5 integrates with Salesforce Sales Cloud to deliver autonomous CRM updates, evidence-based forecasting, and AI coaching. Our integration works with standard and custom objects, and complements Salesforce Einstein with cross-platform intelligence.',
    features: [
      'Full SOQL/SOSL query support for any object',
      'Standard and custom object read/write',
      'Salesforce Flow compatibility',
      'Lightning Web Component ready',
      'Bulk API support for high-volume operations',
      'Complements Einstein AI with cross-platform data',
      'Permission-aware field updates',
      'Complete audit trail and change tracking',
    ],
    useCases: [
      'Automatically update Opportunity stages from buyer signals',
      'Log activities from Gmail/Outlook to Salesforce',
      'Generate forecast reports using evidence from meetings',
      'Enrich Account and Contact records from email data',
      'Coach reps based on deal activity vs top performers',
    ],
    dataFlow: [
      { from: 'Email & Calendar', to: 'Salesforce', description: 'Activities, meeting notes, and deal updates synced automatically' },
      { from: 'Salesforce', to: 'NoQ5', description: 'Opportunity, Account, and Contact data ingested for scoring' },
      { from: 'NoQ5', to: 'Salesforce', description: 'Evidence scores, coaching insights, and field updates written back' },
    ],
    security: [
      'OAuth2 authentication via Salesforce Connected App',
      'Respects Salesforce field-level security and sharing rules',
      'All data encrypted in transit and at rest',
      'GDPR-compliant, EU-based data processing',
      'No data stored outside your security perimeter',
    ],
    faq: [
      { q: 'Does NoQ5 replace Salesforce Einstein?', a: 'No. NoQ5 complements Einstein by bringing in cross-platform signals from email, calendar, and meetings that Einstein cannot access natively.' },
      { q: 'Which Salesforce editions work?', a: 'NoQ5 works with Professional, Enterprise, and Unlimited editions of Salesforce Sales Cloud.' },
      { q: 'Does it respect my sharing rules?', a: 'Yes. NoQ5 respects all Salesforce field-level security, sharing rules, and profile permissions.' },
    ],
  },
  'google-workspace': {
    name: 'Google Workspace',
    logo: '/logos/google-workspace.svg',
    status: 'live',
    seoTitle: 'NoQ5 + Google Workspace | Calendar, Meet & Gmail AI',
    seoDescription: 'Connect Google Calendar, Meet, and Gmail to NoQ5 for automatic meeting intelligence, email analysis, and CRM enrichment. GDPR-compliant.',
    keywords: 'Google Workspace AI, Google Meet transcription, Gmail AI analysis, Google Calendar CRM sync, meeting intelligence',
    tagline: 'Calendar, Meet, and Gmail intelligence for your CRM',
    longDescription: 'NoQ5 connects to Google Workspace to extract deal intelligence from your calendar events, meeting transcripts, and email threads. All signals are used to enrich your CRM and power evidence-based forecasting.',
    features: [
      'Google Calendar event sync for meeting context',
      'Google Meet transcript ingestion and analysis',
      'Gmail thread analysis for deal signals',
      'Contact enrichment from email metadata',
      'Meeting prep with attendee context',
      'Automatic meeting summary generation',
      'Follow-up task extraction from meetings',
      'Multi-participant engagement tracking',
    ],
    useCases: [
      'Auto-generate meeting summaries and action items',
      'Track buyer engagement from email reply patterns',
      'Enrich CRM contacts from Gmail signatures',
      'Prepare deal briefings before meetings',
      'Detect going-dark signals from calendar gaps',
    ],
    dataFlow: [
      { from: 'Google Calendar', to: 'NoQ5', description: 'Meeting schedules and attendee information' },
      { from: 'Google Meet', to: 'NoQ5', description: 'Meeting transcripts and participation data' },
      { from: 'Gmail', to: 'NoQ5', description: 'Email threads, reply patterns, and engagement signals' },
    ],
    security: [
      'Google OAuth2 with minimal scope permissions',
      'No email content stored permanently',
      'Signals extracted and content discarded',
      'GDPR-compliant processing within EU',
      'User-level consent management',
    ],
    faq: [
      { q: 'Does NoQ5 read all my emails?', a: 'No. NoQ5 only processes emails related to deals in your CRM. Personal emails are never accessed.' },
      { q: 'Are meeting transcripts stored?', a: 'Transcripts are processed to extract signals and summaries, then the raw transcript is discarded. Only structured insights are retained.' },
      { q: 'Does it work with Google Workspace for Education?', a: 'NoQ5 is designed for business use. Education accounts may work but are not officially supported.' },
    ],
  },
  'slack': {
    name: 'Slack',
    logo: '/logos/slack.svg',
    status: 'live',
    seoTitle: 'NoQ5 + Slack Integration | Deal Alerts & AI Coaching',
    seoDescription: 'Get real-time deal alerts, AI coaching nudges, and pipeline summaries in Slack. NoQ5 delivers revenue intelligence where your team works.',
    keywords: 'Slack CRM integration, Slack deal alerts, Slack sales coaching, Slack pipeline notifications, Slack AI assistant',
    tagline: 'Deal intelligence delivered where you work',
    longDescription: 'NoQ5 pushes deal alerts, coaching nudges, pipeline summaries, and action items directly to your Slack channels. No more context-switching between CRM and communication tools.',
    features: [
      'Deal stage change notifications',
      'AI coaching nudges in-channel',
      'Pipeline summary reports',
      'Action item reminders',
      'Custom channel routing per team',
      'Going-dark deal alerts',
      'Weekly pipeline digests',
      'Interactive buttons for quick actions',
    ],
    useCases: [
      'Get instant alerts when key deals change status',
      'Receive coaching tips for at-risk deals',
      'Review daily pipeline summaries in team channels',
      'Act on AI recommendations without leaving Slack',
      'Track team performance with automated leaderboards',
    ],
    dataFlow: [
      { from: 'NoQ5', to: 'Slack', description: 'Deal alerts, coaching nudges, and pipeline reports' },
      { from: 'Slack', to: 'NoQ5', description: 'Interactive responses and quick actions from reps' },
    ],
    security: [
      'Slack OAuth2 with minimal bot scopes',
      'Channel-level access controls',
      'No message content read or stored',
      'Outbound notifications only',
      'GDPR-compliant',
    ],
    faq: [
      { q: 'Can I choose which channels get notifications?', a: 'Yes. You can route different alert types to different channels, or use DMs for personal coaching nudges.' },
      { q: 'Does NoQ5 read Slack messages?', a: 'No. The Slack integration is outbound-only. NoQ5 sends notifications but does not read channel messages.' },
    ],
  },
  'pipedrive': {
    name: 'Pipedrive',
    logo: '/logos/pipedrive.svg',
    status: 'live',
    seoTitle: 'NoQ5 + Pipedrive Integration | AI CRM Automation',
    seoDescription: 'Connect NoQ5 to Pipedrive for AI-powered deal automation, forecasting, and coaching. Automatic data capture and pipeline intelligence.',
    keywords: 'Pipedrive AI integration, Pipedrive automation, Pipedrive AI agent, Pipedrive CRM AI, NoQ5 Pipedrive',
    tagline: 'AI-powered deal intelligence for Pipedrive',
    longDescription: 'NoQ5 integrates with Pipedrive to bring autonomous CRM updates, evidence-based forecasting, and AI coaching to your sales team. Perfect for growing teams that need more intelligence from their pipeline.',
    features: [
      'Bi-directional deal and contact sync',
      'Activity logging from email and calendar',
      'Custom field mapping support',
      'Pipeline stage automation',
      'Deal scoring and forecasting',
      'Contact enrichment',
      'Full API v1/v2 support',
      'Webhook-based real-time updates',
    ],
    useCases: [
      'Automatically log emails and meetings to Pipedrive deals',
      'Score deals based on buyer engagement signals',
      'Update deal stages when buyer behavior changes',
      'Generate pipeline forecasts from real evidence',
      'Enrich contacts with data from email interactions',
    ],
    dataFlow: [
      { from: 'Email & Calendar', to: 'Pipedrive', description: 'Activities and meeting notes synced automatically' },
      { from: 'Pipedrive', to: 'NoQ5', description: 'Deal and contact data ingested for AI analysis' },
      { from: 'NoQ5', to: 'Pipedrive', description: 'AI updates, scores, and enriched data written back' },
    ],
    security: [
      'OAuth2 or API key authentication',
      'Encrypted data in transit and at rest',
      'GDPR-compliant EU processing',
      'Granular permission controls',
    ],
    faq: [
      { q: 'Which Pipedrive plans are supported?', a: 'NoQ5 works with all Pipedrive plans that include API access (Professional and above).' },
      { q: 'Can I use custom fields?', a: 'Yes. NoQ5 auto-discovers and maps all custom fields in your Pipedrive account.' },
    ],
  },
  'zoom': {
    name: 'Zoom',
    logo: '/logos/zoom.svg',
    status: 'live',
    seoTitle: 'NoQ5 + Zoom Integration | Meeting Intelligence',
    seoDescription: 'Connect Zoom to NoQ5 for AI meeting summaries, action item extraction, and CRM enrichment from sales calls. GDPR-compliant.',
    keywords: 'Zoom AI integration, Zoom meeting intelligence, Zoom CRM sync, Zoom transcript analysis, sales call intelligence',
    tagline: 'Meeting intelligence from every Zoom call',
    longDescription: 'NoQ5 ingests Zoom meeting recordings and transcripts to extract deal signals, generate summaries, and create action items that sync back to your CRM automatically.',
    features: [
      'Zoom meeting transcript ingestion',
      'AI meeting summary generation',
      'Action item extraction',
      'Participant engagement analysis',
      'Deal signal detection from conversations',
      'Automatic CRM activity logging',
      'Key decision tracking',
      'Follow-up recommendation engine',
    ],
    useCases: [
      'Get AI summaries of every sales call',
      'Extract and assign action items automatically',
      'Track buyer engagement across meetings',
      'Detect deal signals from conversation patterns',
      'Auto-log meeting activities to CRM',
    ],
    dataFlow: [
      { from: 'Zoom', to: 'NoQ5', description: 'Meeting recordings, transcripts, and participant data' },
      { from: 'NoQ5', to: 'CRM', description: 'Meeting summaries, action items, and deal signals' },
    ],
    security: [
      'Zoom OAuth2 with minimal scopes',
      'Transcripts processed and discarded',
      'Only structured insights retained',
      'GDPR-compliant processing',
      'End-to-end encryption support',
    ],
    faq: [
      { q: 'Do I need Zoom Business or higher?', a: 'NoQ5 works with any Zoom plan that includes cloud recordings or the Zoom API.' },
      { q: 'Are recordings stored?', a: 'No. Recordings are processed in real-time. Only structured summaries and signals are retained.' },
    ],
  },
  'microsoft-365': {
    name: 'Microsoft 365',
    logo: '/logos/microsoft-365.svg',
    status: 'coming-soon',
    seoTitle: 'NoQ5 + Microsoft 365 Integration | Outlook & Teams AI',
    seoDescription: 'Coming soon: Connect Microsoft 365, Outlook, and Teams to NoQ5 for AI-powered email intelligence, meeting analysis, and CRM enrichment.',
    keywords: 'Microsoft 365 AI integration, Outlook CRM sync, Teams meeting intelligence, Microsoft 365 sales AI, Outlook AI',
    tagline: 'Outlook, Teams & OneDrive intelligence (coming soon)',
    longDescription: 'NoQ5 will integrate with Microsoft 365 to bring the same AI-powered deal intelligence to Outlook email, Teams meetings, and OneDrive documents. Join the waitlist to get early access.',
    features: [
      'Outlook email thread analysis',
      'Teams meeting transcript ingestion',
      'Calendar sync for meeting context',
      'OneDrive document tracking for deals',
      'Contact enrichment from Outlook data',
      'Microsoft Graph API integration',
    ],
    useCases: [
      'Analyze Outlook email threads for deal signals',
      'Generate meeting summaries from Teams calls',
      'Track document sharing with prospects',
      'Enrich CRM contacts from Outlook data',
      'Sync calendar events with deal timelines',
    ],
    dataFlow: [
      { from: 'Outlook', to: 'NoQ5', description: 'Email engagement signals and reply patterns' },
      { from: 'Teams', to: 'NoQ5', description: 'Meeting transcripts and participation data' },
      { from: 'NoQ5', to: 'CRM', description: 'AI insights, summaries, and enrichment data' },
    ],
    security: [
      'Microsoft OAuth2 via Azure AD',
      'Granular Graph API permissions',
      'GDPR-compliant EU processing',
      'Enterprise-grade security',
    ],
    faq: [
      { q: 'When will Microsoft 365 be available?', a: 'Microsoft 365 integration is in private beta. Join the waitlist to get early access.' },
      { q: 'Which M365 plans will be supported?', a: 'We plan to support Business Standard, Business Premium, and Enterprise plans.' },
    ],
  },
  'linkedin': {
    name: 'LinkedIn',
    logo: '/logos/linkedin.svg',
    status: 'coming-soon',
    seoTitle: 'NoQ5 + LinkedIn Integration | Sales Navigator AI',
    seoDescription: 'Coming soon: Connect LinkedIn Sales Navigator to NoQ5 for AI-powered prospect intelligence, relationship mapping, and CRM enrichment.',
    keywords: 'LinkedIn AI integration, Sales Navigator AI, LinkedIn CRM sync, LinkedIn prospect intelligence, LinkedIn sales tool',
    tagline: 'Prospect intelligence from LinkedIn (coming soon)',
    longDescription: 'NoQ5 will integrate with LinkedIn and Sales Navigator to enrich your CRM with prospect data, track relationship signals, and identify buying committee members automatically.',
    features: [
      'Sales Navigator data enrichment',
      'Buying committee identification',
      'Relationship mapping from connections',
      'Job change alerts for prospects',
      'Company news and trigger events',
      'InMail engagement tracking',
    ],
    useCases: [
      'Enrich CRM contacts with LinkedIn profile data',
      'Map buying committees from company connections',
      'Get alerts when prospects change jobs',
      'Track company news and expansion signals',
      'Identify warm introductions through mutual connections',
    ],
    dataFlow: [
      { from: 'LinkedIn', to: 'NoQ5', description: 'Profile data, company info, and relationship signals' },
      { from: 'NoQ5', to: 'CRM', description: 'Enriched contacts, org charts, and trigger alerts' },
    ],
    security: [
      'LinkedIn OAuth2 authentication',
      'Sales Navigator API compliance',
      'GDPR-compliant data handling',
      'No scraping, only authorized API access',
    ],
    faq: [
      { q: 'When will LinkedIn be available?', a: 'LinkedIn integration is on our roadmap. Join the waitlist for early access.' },
      { q: 'Do I need Sales Navigator?', a: 'Basic LinkedIn integration will work without Sales Navigator. Advanced features will require a Sales Navigator license.' },
    ],
  },
};

const IntegrationDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const integration = slug ? integrationData[slug] : null;

  if (!integration) {
    return (
      <Layout>
        <SEOHead 
          title="NoQ5 Integrations | Connect Your Revenue Stack"
          description="NoQ5 integrates with HubSpot, Salesforce, Google Workspace, Slack, Pipedrive, Zoom, and more. See all integrations."
          canonicalUrl="https://noq5.ai/integrations"
        />
        <div className="min-h-screen bg-background pt-24 pb-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl font-bold text-foreground mb-4">Integration not found</h1>
            <Link to="/integrations"><Button>View All Integrations</Button></Link>
          </div>
        </div>
      </Layout>
    );
  }

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": `NoQ5 + ${integration.name}`,
      "description": integration.seoDescription,
      "url": `https://noq5.ai/integrations/${slug}`,
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "offers": {
        "@type": "Offer",
        "price": "59",
        "priceCurrency": "EUR",
        "description": "Annual subscription, per user per month"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": integration.faq.map(f => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": f.a
        }
      }))
    }
  ];

  return (
    <Layout>
      <SEOHead 
        title={integration.seoTitle}
        description={integration.seoDescription}
        keywords={integration.keywords}
        canonicalUrl={`https://noq5.ai/integrations/${slug}`}
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Hero */}
          <div className="flex items-center gap-6 mb-8">
            <img src={integration.logo} alt={`${integration.name} logo`} className="w-16 h-16" loading="eager" />
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-3xl md:text-4xl font-bold text-foreground">NoQ5 + {integration.name}</h1>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  integration.status === 'live' 
                    ? 'bg-green-500/10 text-green-600 border border-green-500/20' 
                    : 'bg-amber-500/10 text-amber-600 border border-amber-500/20'
                }`}>
                  {integration.status === 'live' ? 'Live' : 'Coming Soon'}
                </span>
              </div>
              <p className="text-lg text-muted-foreground">{integration.tagline}</p>
            </div>
          </div>

          <p className="text-lg text-muted-foreground mb-12">{integration.longDescription}</p>

          {/* Features */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-foreground mb-6">Features</h2>
            <div className="grid md:grid-cols-2 gap-3">
              {integration.features.map((f, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-sm bg-card border border-border">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-foreground text-sm">{f}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Use Cases */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-foreground mb-6">Use Cases</h2>
            <ul className="space-y-3">
              {integration.useCases.map((uc, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Zap className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-foreground">{uc}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Data Flow */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-foreground mb-6">How Data Flows</h2>
            <div className="space-y-4">
              {integration.dataFlow.map((flow, i) => (
                <div key={i} className="flex items-center gap-4 p-4 bg-card border border-border rounded-sm">
                  <span className="font-semibold text-primary whitespace-nowrap">{flow.from}</span>
                  <ArrowRight className="w-5 h-5 text-muted-foreground shrink-0" />
                  <span className="font-semibold text-primary whitespace-nowrap">{flow.to}</span>
                  <span className="text-muted-foreground text-sm">{flow.description}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Security */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
              <Shield className="w-6 h-6" /> Security & Compliance
            </h2>
            <ul className="space-y-3">
              {integration.security.map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-foreground">{s}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* FAQ */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-foreground mb-6">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {integration.faq.map((f, i) => (
                <div key={i}>
                  <h3 className="font-semibold text-foreground mb-2">{f.q}</h3>
                  <p className="text-muted-foreground">{f.a}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center p-8 bg-primary/5 border border-primary/20 rounded-sm">
            <h2 className="text-2xl font-bold text-foreground mb-4">
              {integration.status === 'live' 
                ? `Connect ${integration.name} to NoQ5 today` 
                : `Get early access to ${integration.name} integration`}
            </h2>
            <p className="text-muted-foreground mb-6">See how the integration fits your revenue workflow.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={integration.status === 'live' ? '/?scrollTo=demo' : '/crm-optional'}>
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                  {integration.status === 'live' ? 'Book a Demo' : 'Join Waitlist'} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/integrations">
                <Button size="lg" variant="outline">All Integrations</Button>
              </Link>
            </div>
          </div>

          {/* Other Integrations */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-foreground mb-6">Other Integrations</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(integrationData)
                .filter(([key]) => key !== slug)
                .slice(0, 4)
                .map(([key, integ]) => (
                  <Link key={key} to={`/integrations/${key}`} className="group flex flex-col items-center p-4 border border-border rounded-sm hover:border-primary/50 transition-all">
                    <img src={integ.logo} alt={integ.name} className="w-10 h-10 mb-2" loading="lazy" />
                    <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">{integ.name}</span>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default IntegrationDetailPage;
